import type { AiHelpMessage } from '@buyerflo/core/schemas/aiHelp'
import { Mail } from '@tamagui/lucide-icons-2'
import type { ReactNode } from 'react'
import { SizableText, Spinner, View, XStack, YStack } from 'tamagui'

export function TypingIndicator() {
    return (
        <XStack alignItems="center" gap="$2" paddingVertical="$2" paddingHorizontal="$3">
            <View
                width={32}
                height={32}
                borderRadius={16}
                backgroundColor="$gray4"
                alignItems="center"
                justifyContent="center"
            >
                <Spinner size="small" color="$gray10" />
            </View>
            <SizableText size="$2" color="$gray9">
                Thinking...
            </SizableText>
        </XStack>
    )
}

/**
 * Minimal inline markdown: renders **bold** and *italic* as styled text.
 * Splits the input into segments and wraps matched groups in SizableText with appropriate styles.
 */
function renderMarkdown(text: string, color: string): ReactNode[] {
    // Match **bold** or *italic* segments
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/)
    return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return (
                <SizableText key={i} size="$3" color={color} fontWeight="700">
                    {part.slice(2, -2)}
                </SizableText>
            )
        }
        if (part.startsWith('*') && part.endsWith('*')) {
            return (
                <SizableText key={i} size="$3" color={color} fontStyle="italic">
                    {part.slice(1, -1)}
                </SizableText>
            )
        }
        return (
            <SizableText key={i} size="$3" color={color}>
                {part}
            </SizableText>
        )
    })
}

export default function AiHelpMessageBubble({ message }: { message: AiHelpMessage }) {
    const isUser = message.role === 'user'
    const textColor = isUser ? 'white' : '$color'

    return (
        <YStack gap="$2" alignItems={isUser ? 'flex-end' : 'flex-start'} paddingHorizontal="$3">
            <View
                backgroundColor={isUser ? '$primary' : '$gray3'}
                paddingVertical="$2.5"
                paddingHorizontal="$3"
                borderRadius="$4"
                borderBottomRightRadius={isUser ? '$1' : '$4'}
                borderBottomLeftRadius={isUser ? '$4' : '$1'}
                maxWidth="88%"
            >
                <SizableText
                    size="$3"
                    color={textColor}
                    whiteSpace="pre-wrap"
                >
                    {isUser ? message.content : renderMarkdown(message.content, textColor)}
                </SizableText>
                {!isUser && message.ticketLogged && (
                    <XStack
                        gap="$1.5"
                        alignItems="center"
                        paddingTop="$1.5"
                        borderTopWidth={1}
                        borderTopColor="$gray5"
                        marginTop="$1.5"
                    >
                        <Mail size={14} color="$green10" />
                        <SizableText size="$1" color="$gray9">
                            Support ticket sent - we will follow up via email
                        </SizableText>
                    </XStack>
                )}
            </View>
        </YStack>
    )
}
